import {
	Box,
	Button,
	CardContainer,
	MarkdownRenderer,
	TextSkeleton,
} from "@memorang/ui";
import { useTranslation } from "react-i18next";
import {
	Text as NativeText,
	Platform,
	type StyleProp,
	type ViewStyle,
} from "react-native";
import { Card, TouchableRipple } from "react-native-paper";

type Props = {
	sessionAnalysis?: string;
	loading?: boolean;
	toggleShowCompleteReport: () => void;
	appFont?: string;
	title?: string;
	subtitle?: string;
	icon?: string;
	contentHeight?: number;
	actionLabel?: string;
	containerStyle?: StyleProp<ViewStyle>;
	cardMode?: "elevated" | "outlined" | "contained";
};

type MarkdownNode = {
	key: React.Key;
};

const renderPreviewHeading = (
	node: MarkdownNode,
	children: React.ReactNode[],
) => (
	<NativeText key={node.key} style={{ fontWeight: "700" }}>
		{children}
		{"\n"}
	</NativeText>
);

const SessionAnalysisCard = ({
	sessionAnalysis = "",
	loading = false,
	toggleShowCompleteReport,
	appFont,
	title,
	subtitle,
	icon,
	contentHeight = 150,
	actionLabel,
	containerStyle,
	cardMode,
}: Props) => {
	const { t } = useTranslation();
	const DefaultContent = () => {
		const contentLineHeight = 21;
		const previewLineCount = Math.max(
			1,
			Math.floor((contentHeight - 8) / contentLineHeight),
		);
		const previewRules =
			Platform.OS === "web"
				? {
						body: (node: MarkdownNode, children: React.ReactNode[]) => (
							<NativeText
								key={node.key}
								numberOfLines={previewLineCount}
								style={{ fontSize: 14, lineHeight: contentLineHeight }}
							>
								{children}
							</NativeText>
						),
						heading1: renderPreviewHeading,
						heading2: renderPreviewHeading,
						heading3: renderPreviewHeading,
						heading4: renderPreviewHeading,
						heading5: renderPreviewHeading,
						heading6: renderPreviewHeading,
						paragraph: (node: MarkdownNode, children: React.ReactNode[]) => (
							<NativeText key={node.key}>
								{children}
								{"\n"}
							</NativeText>
						),
					}
				: undefined;

		return (
			<Card.Content
				style={{
					paddingHorizontal: 16,
					height: contentHeight,
					overflow: "hidden",
					paddingTop: 8,
				}}
			>
				<MarkdownRenderer
					text={sessionAnalysis}
					variant="explanation"
					appFont={appFont}
					overrideRules={previewRules}
				/>
			</Card.Content>
		);
	};

	return (
		<CardContainer
			style={containerStyle}
			mode={cardMode}
			title={title ?? t("report.sessionAnalysis.title")}
			subtitle={subtitle ?? t("report.sessionAnalysis.subtitle")}
			icon={icon ?? "head-lightbulb-outline"}
			actions={
				<Card.Actions>
					<Button onPress={toggleShowCompleteReport} mode="text">
						{actionLabel ?? t("report.sessionAnalysis.viewFullAnalysis")}
					</Button>
				</Card.Actions>
			}
		>
			<TouchableRipple
				onPress={toggleShowCompleteReport}
				rippleColor={"transparent"}
			>
				{loading || !sessionAnalysis ? (
					<Box
						paddingVertical={16}
						paddingHorizontal={16}
						height={contentHeight}
						alignItems="center"
					>
						<TextSkeleton numLines={4} height={18} />
					</Box>
				) : (
					<DefaultContent />
				)}
			</TouchableRipple>
		</CardContainer>
	);
};

export default SessionAnalysisCard;
